import { Stack, Text } from '@mantine/core'
import { type NotificationData, notifications } from '@mantine/notifications'

import CheckInCircleSuccess from '@/assets/icons/check-in-circle-success.svg'
import CrossInCircleError from '@/assets/icons/cross-circle-fill.svg'
import WarnCircleFill from '@/assets/icons/exclamation-circle-fill.svg'
import InfoCircleFill from '@/assets/icons/info-circle-fill.svg'

////////////////////
// Types

interface CustomNotificationProps extends Omit<NotificationData, 'title' | 'message'> {
  title: string
  cta?: {
    onClick: () => void
    text: string
  }
  message?: string
}

////////////////////
// Constants

const DEFAULT_DURATION = 4000
const ICON_SIZE = 20

////////////////////
// Private Functions

const getMessage = (props: {
  cta?: {
    onClick: () => void
    text: string
  }
  dataTestId: string
  message?: string
}) => {
  const { cta, dataTestId, message } = props
  return (
    <Stack gap={8}>
      <Text
        variant="body2"
        mt={message ? 'var(--mantine-spacing-2xs)' : 0}
        data-testid={dataTestId}
      >
        {message}
      </Text>
      {cta ? (
        <Text
          variant={'body2'}
          c={'var(--text-active)'}
          onClick={cta.onClick}
          style={{ cursor: 'pointer' }}
        >
          {cta.text}
        </Text>
      ) : (
        <></>
      )}
    </Stack>
  )
}

////////////////////
// Public Functions

export const showInfoNotification = ({
  title,
  cta,
  message,
  autoClose = DEFAULT_DURATION,
  withCloseButton = true,
  ...restProps
}: CustomNotificationProps) => {
  notifications.show({
    icon: (
      <InfoCircleFill
        width={ICON_SIZE}
        height={ICON_SIZE}
        fill="var(--common-primary)"
        data-testid="infoNotification__icon"
      />
    ),
    title: (
      <Text variant="subhead3" data-testid="infoNotification__title">
        {title}
      </Text>
    ),
    message: getMessage({
      cta,
      dataTestId: `infoNotification__message`,
      message,
    }),
    autoClose,
    withCloseButton,
    ...restProps,
  })
}

export const showSuccessNotification = ({
  title,
  cta,
  message,
  autoClose = DEFAULT_DURATION,
  withCloseButton = true,
  ...restProps
}: CustomNotificationProps) => {
  notifications.show({
    icon: (
      <CheckInCircleSuccess
        width={ICON_SIZE}
        height={ICON_SIZE}
        fill="var(--text-success)"
        data-testid="successNotification__icon"
      />
    ),
    title: (
      <Text variant="subhead3" data-testid="successNotification__title">
        {title}
      </Text>
    ),
    message: getMessage({
      cta,
      dataTestId: `successNotification__message`,
      message,
    }),
    autoClose,
    withCloseButton,
    ...restProps,
  })
}

export const showWarningNotification = ({
  title,
  cta,
  message,
  autoClose = DEFAULT_DURATION,
  withCloseButton = true,
  ...restProps
}: CustomNotificationProps) => {
  notifications.show({
    icon: (
      <WarnCircleFill
        width={ICON_SIZE}
        height={ICON_SIZE}
        fill="var(--bg-status-pending-bold)"
        data-testid="warningNotification__icon"
      />
    ),
    title: (
      <Text variant="subhead3" data-testid="warningNotification__title">
        {title}
      </Text>
    ),
    message: getMessage({
      cta,
      dataTestId: `warningNotification__message`,
      message,
    }),
    autoClose,
    withCloseButton,
    ...restProps,
  })
}

export const showErrorNotification = ({
  title,
  cta,
  message,
  autoClose = DEFAULT_DURATION,
  withCloseButton = true,
  ...restProps
}: CustomNotificationProps) =>
  notifications.show({
    icon: (
      <CrossInCircleError
        width={ICON_SIZE}
        height={ICON_SIZE}
        data-testid="errNotification__icon"
        fill="var(--text-danger)"
      />
    ),
    title: (
      <Text variant="subhead3" data-testid="errNotification__title">
        {title}
      </Text>
    ),
    message: getMessage({
      cta,
      dataTestId: `errNotification__message`,
      message,
    }),
    autoClose,
    withCloseButton,
    ...restProps,
  })
